import { Formik } from 'formik';
import { useCallback, useState } from 'react';
import { array, mixed, object, string } from 'yup';

import { useGetLanguagesQuery } from '@app/api/languages';
import { useCreateProjectMutation } from '@app/api/newProject';
import { showUnexpectedErrorToast } from '@app/components/Toast';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import useFeatureFlag from '../../../../pages/ProjectsList/hooks/useFeatureFlag';
import { ProjectType } from '../../../../types/project';
import type { CreateFormProps } from './CreateForm';
import CreateForm from './CreateForm';

type ErrorResponseViolation = {
    [key: string]: string[];
};

type ErrorResponse = {
    error: {
        data: {
            error: {
                message: string;
                code: number;
                violations: ErrorResponseViolation;
            };
        };
    };
    status: number;
};

export const LanguagesSchema = object().shape({
    sourceLanguage: object().shape({
        label: string(),
        value: string().required('Base language is required'),
    }),
    targetLanguages: array()
        .of(
            object({
                label: string(),
                value: string().required(),
            }),
        )
        .min(1, 'At least one target language is required'),
});

export const ProjectNameSchema = object().shape({
    name: string().max(255, 'Project names maximum length is 255').required('Project name is required'),
});

const CreateProjectSchema = ProjectNameSchema.concat(LanguagesSchema).concat(
    object().shape({
        segmentationRulesType: string().when('isSegmentationEnabled', {
            is: true,
            then: string().required(),
        }),
        segmentationRulesFile: mixed().when('segmentationRulesType', {
            is: 'custom',
            then: mixed()
                .required('A SRX file is required for setting up custom rules.')
                .test(
                    'segmentationRulesFile',
                    'We only accept SRX file types for setting up custom rules.',
                    (value) => value && value.name && value.name.split('.').pop() === 'srx',
                ),
        }),
    }),
);

type LanguageOption = {
    value: string;
    label: string;
};

const initialSourceLanguage: LanguageOption = { value: '640', label: 'English (en)' };

export type SubmitValues = {
    name: string;
    sourceLanguage: LanguageOption;
    targetLanguages: LanguageOption[];
    projectType: string;
    isSegmentationEnabled: boolean;
    onboardingFlow: boolean;
    segmentationRulesType: string;
    segmentationRulesFile: File | undefined;
};

export const initialFormValues: SubmitValues = {
    name: '',
    sourceLanguage: initialSourceLanguage,
    targetLanguages: [],
    projectType: '0',
    isSegmentationEnabled: false,
    onboardingFlow: false,
    segmentationRulesType: 'default',
    segmentationRulesFile: undefined,
};

type CreateFormContainerProps = Omit<CreateFormProps, 'onSubmit' | 'loading' | 'languagesLoading' | 'languages'> & {
    initialValues?: typeof initialFormValues;
    onSubmit?: (projectId: string) => void;
    loading?: boolean;
    selectedProjectType?: string | null;
    goBackToSelectionScreen?: () => void;
};

const CreateFormContainer = ({
    initialValues = initialFormValues,
    minified = false,
    onSubmit,
    loading = false,
    selectedProjectType = null,
    ...rest
}: CreateFormContainerProps) => {
    const [createProjectLoading, setCreateProjectLoading] = useState(false);
    const { isLoading: languagesLoading, data: languages } = useGetLanguagesQuery(undefined);
    const [createProject] = useCreateProjectMutation();
    const { newProjectCreationModal } = useFeatureFlag();

    const submitHandler = useCallback(
        async (
            {
                name,
                sourceLanguage,
                targetLanguages,
                projectType,
                isSegmentationEnabled,
                onboardingFlow,
                segmentationRulesType,
                segmentationRulesFile,
            }: SubmitValues,
            setFieldError: (field: string, message: string) => void,
        ) => {
            setCreateProjectLoading(true);
            const response = await createProject({
                name,
                sourceLanguage: parseInt(sourceLanguage.value, 10),
                targetLanguages: targetLanguages.map(({ value }) => parseInt(value, 10)),
                projectType: parseInt(projectType, 10),
                reviewing: true,
                isSegmentationEnabled,
                onboardingFlow,
                segmentationRulesType,
                segmentationRulesFile,
            });

            if ('error' in response) {
                const violations = (response as ErrorResponse)?.error.data.error?.violations as ErrorResponseViolation;

                if (violations && 'segmentationRulesFile' in violations) {
                    setFieldError('segmentationRulesFile', violations.segmentationRulesFile.toString());
                } else {
                    showUnexpectedErrorToast();
                }

                setCreateProjectLoading(false);

                return;
            }

            const { baseLanguage, reviewing, projectName, projectId, projectRole } = response.data;

            const projectTypeView = parseInt(projectType, 10) ? 'documents' : 'localization';

            if (process.env.NODE_ENV !== 'test') {
                track(AnalyticsEventName.PROJECT_CREATED, {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    base_language: baseLanguage,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    project_type: projectTypeView,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    project_name: projectName,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    project_id: projectId,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    project_role: projectRole,
                    reviewing,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    is_segmentation_enabled: isSegmentationEnabled,
                });
            }

            // default submit behavior
            if (!onSubmit) {
                if (projectType === ProjectType.pagedDocuments && newProjectCreationModal) {
                    window.location.assign(`/upload/${projectId}/`);
                } else {
                    window.location.assign(`/project/${projectId}/?view=multi`);
                }
            }

            if (onSubmit) {
                onSubmit?.(projectId);
            }
        },
        [createProject, newProjectCreationModal, onSubmit],
    );

    return (
        <Formik<SubmitValues>
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={CreateProjectSchema}
            onSubmit={(values, { setFieldError }) => {
                submitHandler(values, setFieldError);
            }}
        >
            <CreateForm
                loading={createProjectLoading || loading}
                languages={languages || []}
                languagesLoading={languagesLoading}
                minified={minified}
                selectedProjectType={selectedProjectType}
                {...rest}
            />
        </Formik>
    );
};

export default CreateFormContainer;
